/**
 * Task commands and Tasks-app navigation. The old dashboard tab id remains
 * only so the shell can remove it from pre-app-switcher persisted tab sets.
 */
import { create } from "zustand";
import { ctxFor, getData } from "@/data/store";
import { canEditIn } from "@/commands/nav";
import { useUi } from "@/store/ui";
import { registerCommand } from "./registry";
import { openItem } from "./nav";
import { toast } from "@/ui/toast";

export const TASKS_DASHBOARD_TAB = "tasks:dashboard";

export function openTasksDashboard(): void {
  useUi.getState().openTaskView("overview");
}

/** Create a task list (personal by default; any writable mounted space). */
export function createTaskListIn(parentId: string | null, spaceId?: string): string | null {
  const ctx = spaceId ? (ctxFor(spaceId) ?? getData()) : getData();
  if (!canEditIn(ctx)) {
    toast.error("You have view-only access to this folder.");
    return null;
  }
  const id = ctx.workspace.createItem("tasklist", parentId, "");
  openItem(id, { newTab: true });
  return id;
}

export function createTaskList(parentId: string | null = null): string | null {
  return createTaskListIn(parentId);
}

/** One-shot "scroll to and flash this task" signal for TaskListView. */
interface TaskFocusState {
  taskId: string | null;
  at: number;
  focus: (taskId: string) => void;
  consume: () => void;
}

export const useTaskFocus = create<TaskFocusState>((set) => ({
  taskId: null,
  at: 0,
  focus: (taskId) => set({ taskId, at: Date.now() }),
  consume: () => set({ taskId: null }),
}));

export function focusTask(taskId: string): void {
  useTaskFocus.getState().focus(taskId);
}

registerCommand({
  id: "tasks.panel",
  title: "Open Tasks",
  defaultKeys: "$mod+Shift+t",
  section: "Tasks",
  run: () => useUi.getState().setActiveApp("tasks"),
});

registerCommand({
  id: "tasks.dashboard",
  title: "Open tasks dashboard",
  section: "Tasks",
  run: () => openTasksDashboard(),
});

registerCommand({
  id: "tasks.newList",
  title: "New task list",
  section: "Tasks",
  run: () => void createTaskList(null),
});

registerCommand({
  id: "tasks.add",
  title: "Add task",
  defaultKeys: "$mod+Shift+a",
  section: "Tasks",
  run: () => {
    const ui = useUi.getState();
    if (ui.activeTaskView === "overview") {
      const fallback = getData().workspace
        .getSnapshot()
        .find((item) => item.type === "tasklist" && item.builtin === "tasks" && !item.trashedAt);
      if (fallback) ui.openTaskView(fallback.id);
      else ui.setActiveApp("tasks");
    } else {
      ui.setActiveApp("tasks");
    }
    setTimeout(() => {
      document
        .querySelector<HTMLInputElement>("[data-task-list-view] input[type='text']")
        ?.focus();
    }, 50);
  },
});
